<script setup>
import {
  onMounted,
  onBeforeUnmount,
  ref
} from 'vue'

const video = ref(null)

const connected = ref(false)
const error = ref('')

let pc = null
let reconnectTimer = null
let stopped = false
let connecting = false


async function start() {

  // Ne pokušavaj ako je komponenta ugašena
  // ili je već u toku drugi pokušaj.
  if (stopped || connecting) {
    return
  }

  connecting = true

  try {

    error.value = ''
    connected.value = false


    // Zatvori prethodnu WebRTC konekciju
    if (pc) {

      try {
        pc.close()
      } catch (_) {
        console.log('PeerConnection close error')
    }

      pc = null
    }


    // Novi WebRTC PeerConnection
    pc = new RTCPeerConnection()


    // Kada MediaMTX pošalje video/audio track
    pc.ontrack = (event) => {

      console.log(
        'WebRTC track received'
      )

      if (
        video.value &&
        event.streams &&
        event.streams.length > 0
      ) {

        video.value.srcObject =
          event.streams[0]
      }
    }


    // Praćenje WebRTC stanja
    pc.onconnectionstatechange = () => {

      if (!pc) {
        return
      }

      console.log(
        'WebRTC state:',
        pc.connectionState
      )


      if (
        pc.connectionState === 'connected'
      ) {

        connected.value = true
        error.value = ''

        console.log(
          'WebRTC CONNECTED'
        )
      }


      if (
        pc.connectionState === 'failed' ||
        pc.connectionState === 'disconnected'
      ) {

        connected.value = false

        console.log(
          'WebRTC connection lost'
        )

        scheduleReconnect()
      }
    }


    // Primamo video
    pc.addTransceiver(
      'video',
      {
        direction: 'recvonly'
      }
    )


    // Primamo audio
    pc.addTransceiver(
      'audio',
      {
        direction: 'recvonly'
      }
    )


    // Kreiraj SDP offer
    const offer =
      await pc.createOffer()


    await pc.setLocalDescription(
      offer
    )


    // Sačekaj ICE gathering
    await waitForIceGathering()


    if (
      !pc.localDescription ||
      !pc.localDescription.sdp
    ) {

      throw new Error(
        'WebRTC SDP nije generisan'
      )
    }


    console.log(
      'Sending WHEP request'
    )


    // WHEP preko Caddy HTTPS proxyja
    const response =
      await fetch(
        '/camera/watch/whep',
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/sdp'
          },

          body:
            pc.localDescription.sdp
        }
      )


    /*
     * 404 znači da MediaMTX trenutno
     * nema aktivan stream "watch".
     *
     * To nije nužno greška aplikacije.
     */
    if (!response.ok) {

      if (response.status === 404) {

        throw new Error(
          `Aplikacija je izgašena ili je potrebno ponovo pokrenuti (${response.status})`
            //`WHEP error: ${response.status}`
        )
      }

      throw new Error(
        `WHEP error: ${response.status}`
      )
    }


    // SDP odgovor od MediaMTX-a
    const answer =
      await response.text()


    if (!answer) {

      throw new Error(
        'MediaMTX je vratio prazan WHEP odgovor'
      )
    }


    // Postavi WebRTC answer
    await pc.setRemoteDescription({
      type: 'answer',
      sdp: answer
    })


    console.log(
      'WHEP connection established'
    )


  } catch (e) {

    console.error(
      'WebRTC error:',
      e
    )

    connected.value = false

    error.value =
      e?.message ||
      String(e)

    // Automatski pokušaj ponovo
    scheduleReconnect()


  } finally {

    connecting = false
  }
}


/*
 * Automatski reconnect svakih 3 sekunde.
 */
function scheduleReconnect() {

  if (stopped) {
    return
  }


  // Već postoji zakazan reconnect
  if (reconnectTimer) {
    return
  }


  console.log(
    'WebRTC reconnect in 3 seconds...'
  )


  reconnectTimer =
    setTimeout(() => {

      reconnectTimer = null

      start()

    }, 3000)
}


/*
 * Čeka da browser završi ICE gathering.
 */
function waitForIceGathering() {

  return new Promise((resolve) => {

    if (!pc) {

      resolve()
      return
    }


    if (
      pc.iceGatheringState === 'complete'
    ) {

      resolve()
      return
    }


    const checkState = () => {

      if (!pc) {

        resolve()
        return
      }


      if (
        pc.iceGatheringState === 'complete'
      ) {

        pc.removeEventListener(
          'icegatheringstatechange',
          checkState
        )

        resolve()
      }
    }


    pc.addEventListener(
      'icegatheringstatechange',
      checkState
    )
  })
}


/*
 * Zaustavi WebRTC i reconnect kada
 * korisnik napusti CameraView.
 */
function stop() {

  stopped = true


  // Zaustavi reconnect timer
  if (reconnectTimer) {

    clearTimeout(
      reconnectTimer
    )

    reconnectTimer = null
  }


  // Zatvori PeerConnection
  if (pc) {

    try {
      pc.close()
    } catch (_) {
    console.log('PeerConnection close error')
    }

    pc = null
  }


  // Očisti video
  if (video.value) {

    video.value.srcObject = null
  }


  connected.value = false

  connecting = false
}


/*
 * Pokreni WebRTC kada se CameraView otvori.
 */
onMounted(() => {

  stopped = false

  start()
})


/*
 * Očisti sve kada se CameraView napusti.
 */
onBeforeUnmount(() => {

  stop()
})
</script>


<template>

  <div class="camera-container">

    <video
      ref="video"
      autoplay
      playsinline
      muted
      controls
      class="camera-video"
    ></video>


    <!-- Status kada video nije povezan -->
    <div
      v-if="!connected"
      class="camera-status"
    >

      {{ error || 'Connecting...' }}

    </div>


    <!-- LIVE indikator -->
    <div
      v-if="connected"
      class="live-indicator"
    >

      ● LIVE

    </div>

  </div>

</template>


<style scoped>

.camera-container {

  position: fixed;

  inset: 0;

  width: 100vw;
  height: 100vh;

  background: black;

  display: flex;

  align-items: center;

  justify-content: center;

  overflow: hidden;
}


.camera-video {

  width: 100%;
  height: 100%;

  object-fit: contain;

  background: black;
}


.camera-status {

  position: absolute;

  inset: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  padding: 20px;

  color: white;

  font-size: 20px;

  text-align: center;

  background: rgba(0, 0, 0, 0.35);
}


.live-indicator {

  position: absolute;

  top: 15px;
  left: 15px;

  padding: 6px 10px;

  background: rgba(0, 0, 0, 0.7);

  color: white;

  border-radius: 5px;

  font-size: 14px;

  font-weight: bold;
}

</style>